<template>
  <div class="min-h-view h-view w-full overflow-auto bg-white text-black">
    <!-- DESKTOP VERSION -->
    <div class="mx-auto hidden max-w-(--breakpoint-lg) flex-row md:flex">
      <div class="min-h-view mr-3 flex w-1/6 flex-col pt-16 pl-4">
        <button
          v-for="(infoTab, idx) in informationArticles"
          :key="idx"
          @click="toggleActive(idx)"
          class="text-md cursor-pointer p-1 text-left font-medium focus:outline-hidden"
          :class="[idx == tabIndex ? 'text-ul-orange-400 font-bold' : '']"
        >
          {{ infoTab.title }}
        </button>
      </div>
      <div class="border-ul-orange-400 my-16 border"></div>
      <div class="h-view w-5/6 overflow-auto p-16 text-black">
        <div class="flex flex-row items-center space-x-4 pb-4">
          <PhosphorIcon :iconName="activeTab.icon || 'info'" class="text-2xl" />
          <h1 class="text-xl font-bold">{{ activeTab.title }}</h1>
        </div>
        <div class="v-html" v-html="activeTab.text" />
      </div>
    </div>
    <!-- MOBILE VERSION -->
    <div class="sm:mx-8 sm:my-4 md:hidden">
      <div v-for="(infoTab, idx) in informationArticles" :key="idx" class="border-ul-orange-300 w-full border-b">
        <button
          @click="toggleActive(idx)"
          class="relative flex h-16 w-full items-center text-lg"
          :class="idx === tabIndex && isOpen ? 'bg-ul-orange-200 font-semibold' : ''"
        >
          <PhosphorIcon :iconName="infoTab.icon || 'info'" class="mx-5 text-2xl" />
          <div class="flex h-16 grow items-center">
            <span>{{ infoTab.title }}</span>
          </div>
          <!-- Chevron (arrow) -->
          <svg
            viewBox="0 0 48 48"
            class="m-4 h-8 w-8 duration-200"
            :class="idx === tabIndex && isOpen ? 'text-ul-orange-400 -rotate-180' : 'text-ul-orange-300'"
          >
            <path fill="currentColor" d="m14.83,16.42l9.17,9.17l9.17,-9.17l2.83,2.83l-12,12l-12,-12l2.83,-2.83z" />
          </svg>
        </button>
        <div
          class="ul-scroll bg-ul-orange-200 bg-opacity-25 overflow-x-hidden overflow-y-scroll text-black shadow-inner transition-all duration-300"
          :class="idx === tabIndex && isOpen ? 'max-h-1/2-screen' : 'max-h-0'"
        >
          <div class="v-html p-4" v-html="infoTab.text" />
        </div>
      </div>
    </div>
    <PageFooter />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { computed } from 'vue'
import { watch } from 'vue'
import { onMounted } from 'vue'

import { useRoute } from 'vue-router'
import PageFooter from '../components/PageFooter.vue'
import PhosphorIcon from '../components/PhosphorIcon.vue'
import { useInformationStore } from '../stores/information'

const informationStore = useInformationStore()

const tabIndex = ref(0)
// boolean for whether mobile menu is open
const isOpen = ref(false)

const informationArticles = computed(() => informationStore.articles.toSorted((a, b) => b.priority - a.priority))
const activeTab = computed(
  () =>
    informationArticles.value[tabIndex.value] ?? {
      icon: '',
      title: '',
      text: '',
      priority: 0,
    }
)

function slugifyString(value: string) {
  return value
    .trim()
    .toLocaleLowerCase()
    .replace('æ', 'ae')
    .replace('ø', 'oe')
    .replace('å', 'aa')
    .replace('+', 'pluss')
    .replace(/\s/g, '-')
    .replace(/[^a-z-]/g, '')
}
const slugifiedTabNames = computed(() => informationArticles.value.map((info) => slugifyString(info.title)))

function toggleActive(index: number) {
  if (tabIndex.value === index && isOpen.value) {
    isOpen.value = false
  } else {
    isOpen.value = true
    tabIndex.value = index
  }
}

function goToHashTab() {
  const hashValue = window.location.hash.replace('#', '')
  const index = slugifiedTabNames.value.findIndex((tabName) => tabName === hashValue)
  tabIndex.value = Math.max(index, 0)
}

const route = useRoute()
watch(route, goToHashTab)

watch(tabIndex, (newValue: number) => {
  window.location.hash = slugifiedTabNames.value[newValue] ?? undefined
})

onMounted(() => {
  goToHashTab()
})
</script>

<style lang="scss"></style>
